import React from 'react';
import { Box, Typography } from '@mui/material';
import { GUIDE_PHASES, type GuidePhase } from '../content/phantomContent';
import { GUIDE_ACCENT, guideTokens } from '../theme/guideTokens';

interface GuideProgressProps {
  activePhase: GuidePhase;
  onSelectPhase?: (phase: GuidePhase) => void;
}

export const GuideProgress: React.FC<GuideProgressProps> = ({ activePhase, onSelectPhase }) => {
  const activeIndex = GUIDE_PHASES.findIndex((p) => p.id === activePhase);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        mb: 3,
        p: 1.25,
        borderRadius: `${guideTokens.radiusCard}px`,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: (t) =>
          t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      }}
      role="navigation"
      aria-label="Guide progress"
    >
      {GUIDE_PHASES.map((phase, index) => {
        const active = phase.id === activePhase;
        const done = index < activeIndex;
        return (
          <React.Fragment key={phase.id}>
            {index > 0 && (
              <Typography
                aria-hidden
                sx={{
                  color: 'text.disabled',
                  fontSize: 12,
                  px: 0.25,
                  display: { xs: 'none', sm: 'inline' },
                }}
              >
                →
              </Typography>
            )}
            <Box
              component="button"
              type="button"
              onClick={() => onSelectPhase?.(phase.id)}
              sx={{
                all: 'unset',
                cursor: onSelectPhase ? 'pointer' : 'default',
                px: 1.25,
                py: 0.6,
                borderRadius: 999,
                fontFamily: guideTokens.fontDisplay,
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: active ? '#fff' : done ? GUIDE_ACCENT : 'text.secondary',
                bgcolor: active
                  ? GUIDE_ACCENT
                  : done
                    ? (t) => guideTokens.accentSoft(t.palette.mode === 'dark')
                    : 'transparent',
                border: '1px solid',
                borderColor: active
                  ? GUIDE_ACCENT
                  : done
                    ? (t) => guideTokens.accentBorder(t.palette.mode === 'dark')
                    : 'divider',
                transition: 'background .15s ease, color .15s ease',
              }}
            >
              {phase.label}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};
